import { useEffect, useRef, useState } from 'react';
import { apm } from '@elastic/apm-rum';
import {
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';

import { useAxiosWithAPI } from '../../../common/hooks/useAxiosWithAPI';
import { ReactQueryKeys } from '../../../common/react-query-keys';
import { getOrderStatus } from '../api/paymentOrderApi';
import {
  OrderStatusResponse,
  PaymentOrderStatus,
  PaymentOrderType,
  RechargeOrderStatusResp,
  VirtualProductOrderStatusResp,
} from '../types';

const POLL_INTERVAL_IN_MS = 2000;

type UsePollOrderStatusParams<T extends { orderStatus: string }> = {
  orderNumber: string | null;
  orderType: PaymentOrderType;
  onCompleted?: (data: T) => void;
  onError?: (error: Error) => void;
  isTerminal?: (data: T) => boolean;
};

const isTerminalStatus = (status: string): boolean =>
  status === PaymentOrderStatus.COMPLETED ||
  status === PaymentOrderStatus.FAILED ||
  status === PaymentOrderStatus.CLOSED;

const getTerminalState = <T extends { orderStatus: string }>(
  order: T | null,
  isTerminal?: (data: T) => boolean
): boolean => {
  if (!order) return false;
  const terminalByOrderStatus = isTerminalStatus(order.orderStatus);
  const terminalByCustomPredicate = isTerminal?.(order) ?? false;
  return terminalByOrderStatus || terminalByCustomPredicate;
};

const extractOrder = (
  resp: { virtualProductOrder: unknown; rechargeOrder: unknown },
  orderType: PaymentOrderType
): RechargeOrderStatusResp | VirtualProductOrderStatusResp | null => {
  if (orderType === PaymentOrderType.RECHARGE) {
    return resp.rechargeOrder as RechargeOrderStatusResp | null;
  }
  return resp.virtualProductOrder as VirtualProductOrderStatusResp | null;
};

const useSyncVirtualProductOrderDetails = (
  rawData: OrderStatusResponse | undefined,
  orderNumber: string | null,
  orderType: PaymentOrderType
) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (
      rawData &&
      orderType === PaymentOrderType.VIRTUAL_PRODUCT &&
      orderNumber
    ) {
      queryClient.setQueryData(
        ReactQueryKeys.virtualProductOrderDetails(orderNumber),
        rawData
      );
    }
  }, [rawData, orderType, orderNumber, queryClient]);
};

export const usePollOrderStatus = <T extends { orderStatus: string }>({
  orderNumber,
  orderType,
  onCompleted,
  onError,
  isTerminal,
}: UsePollOrderStatusParams<T>) => {
  const axios = useAxiosWithAPI();
  const queryClient = useQueryClient();

  const onCompletedRef = useRef(onCompleted);
  const onErrorRef = useRef(onError);
  const isTerminalRef = useRef(isTerminal);
  isTerminalRef.current = isTerminal;

  onCompletedRef.current = onCompleted;
  onErrorRef.current = onError;

  const [isPolling, setIsPolling] = useState(true);

  useEffect(() => {
    setIsPolling(true);
  }, [orderNumber]);

  const queryKey = ReactQueryKeys.orderStatus(orderNumber || '');

  const {
    data: rawData,
    error,
    isLoading,
  } = useQuery({
    queryKey,
    queryFn: () => {
      if (!orderNumber) {
        throw new Error('orderNumber is required to poll order status');
      }
      return getOrderStatus(axios, orderNumber, orderType);
    },
    enabled: Boolean(orderNumber),
    refetchInterval: query => {
      if (!isPolling) return false;
      if (!isTerminalRef.current) return POLL_INTERVAL_IN_MS;
      const latestRawData = query.state.data as OrderStatusResponse | undefined;
      const latestOrder = latestRawData
        ? (extractOrder(latestRawData, orderType) as T | null)
        : null;
      return getTerminalState(latestOrder, isTerminalRef.current)
        ? false
        : POLL_INTERVAL_IN_MS;
    },
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
  });

  const order = rawData ? (extractOrder(rawData, orderType) as T | null) : null;

  useSyncVirtualProductOrderDetails(rawData, orderNumber, orderType);

  useEffect(() => {
    if (error) {
      apm.captureError(error);
      onErrorRef.current?.(error);
    }
  }, [error]);

  const hasCompletedRef = useRef(false);

  useEffect(() => {
    if (!order) return;
    if (!getTerminalState(order, isTerminalRef.current)) return;
    setIsPolling(false);
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;
    onCompletedRef.current?.(order);
  }, [order]);

  useEffect(
    () => () => {
      setIsPolling(false);
    },
    []
  );

  const stopPolling = () => {
    setIsPolling(false);
    queryClient.cancelQueries({
      queryKey,
    });
  };

  return { data: order, isLoading, stopPolling };
};

const EMPTY_ORDER_STATUS_RESPONSE: OrderStatusResponse = {
  virtualProductOrder: null,
  rechargeOrder: null,
};

export const usePollOrderStatusSuspense = <T extends { orderStatus: string }>({
  orderNumber,
  orderType,
  onCompleted,
  isTerminal,
}: UsePollOrderStatusParams<T>) => {
  const axios = useAxiosWithAPI();
  const queryClient = useQueryClient();

  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  const isTerminalRef = useRef(isTerminal);
  isTerminalRef.current = isTerminal;

  const [isPolling, setIsPolling] = useState(true);

  useEffect(() => {
    setIsPolling(true);
  }, [orderNumber]);

  const isActive = Boolean(orderNumber);
  const queryKey = ReactQueryKeys.orderStatus(orderNumber || 'inactive');

  const { data: rawData } = useSuspenseQuery({
    queryKey,
    queryFn: () => {
      if (!orderNumber) {
        return EMPTY_ORDER_STATUS_RESPONSE;
      }
      return getOrderStatus(axios, orderNumber, orderType);
    },
    refetchInterval: query => {
      if (!isActive || !isPolling) return false;
      if (!isTerminalRef.current) return POLL_INTERVAL_IN_MS;
      const latestRawData = query.state.data as OrderStatusResponse | undefined;
      const latestOrder = latestRawData
        ? (extractOrder(latestRawData, orderType) as T | null)
        : null;
      return getTerminalState(latestOrder, isTerminalRef.current)
        ? false
        : POLL_INTERVAL_IN_MS;
    },
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
  });

  const order = rawData ? (extractOrder(rawData, orderType) as T | null) : null;

  useSyncVirtualProductOrderDetails(rawData, orderNumber, orderType);

  const hasCompletedRef = useRef(false);

  useEffect(() => {
    if (!order) return;
    if (!getTerminalState(order, isTerminalRef.current)) return;
    setIsPolling(false);
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;
    onCompletedRef.current?.(order);
  }, [order]);

  useEffect(
    () => () => {
      setIsPolling(false);
    },
    []
  );

  const stopPolling = () => {
    setIsPolling(false);
    queryClient.cancelQueries({
      queryKey,
    });
  };

  return { data: order, stopPolling };
};
